/** Central copy for Admin form FieldHelp tooltips. */

export const catalogHelp = {
  typeName:
    'Human-readable product name shown in Admin and apps (e.g. Atmos). Must be unique enough for operators to recognize.',
  typeAliases:
    'Optional alternate names used by ingestion, mobile, or legacy data. Comma-separated. Leave empty if none.',
  description: 'Short explanation of what this device measures or controls.',
  manufacturer: 'Hardware vendor. Pick from suggestions or type a new value.',
  model:
    'Commercial model/SKU. Prefer an existing model from the list, or use + to create a new one.',
  hardwareVersion:
    'Fixed HW revision for this catalog row (e.g. 1.0). Firmware versions are tracked separately.',
  category:
    'Operator-defined grouping for filters/dashboards. Reuse an existing category when possible.',
  status:
    'ACTIVE = can be manufactured/registered; DEPRECATED = legacy only; DISABLED = do not use for new devices.',
  wirelessSection:
    'Modules are radios on the board (Wi‑Fi, BLE, LoRa, Cellular). Protocols ride on top: Wi‑Fi/BLE/Cellular require MQTT; LoRa requires LoRaWAN. Select every radio and protocol this hardware supports.',
  wirelessModules:
    'Radios on the board. Selecting Wi‑Fi, BLE, or Cellular requires MQTT; LoRa requires LoRaWAN.',
  wirelessProtocols:
    'Application protocols over the radios. MQTT for Wi‑Fi/BLE/Cellular; LoRaWAN for LoRa.',
  supportedVersions: 'Firmware versions this type accepts, comma-separated.',
  minimumVersion: 'Oldest firmware still considered supported.',
  latestVersion: 'Newest approved firmware for OTA / ops reference.',
  updateChannel: 'Release track: stable (production), beta, or legacy.',
  displaySection:
    'Has physical display = hardware includes a screen. Display reference = part/module ID when present. Color display = screen supports color.',
  hasDisplay: 'Check if the hardware has a screen.',
  displayReference: 'Part number or internal display module ID (when has display).',
  hasColor: 'Whether the screen supports color.',
  featuresSection:
    'Capability flags used by apps and APIs: Allows external sensor; Supports battery; Supports alerts; Supports history; Supports location; Supports calibration; Supports FW OTA. Enable only what this hardware/software actually supports.',
  featureAllowsExternalSensor: 'Whether this type can attach external sensors.',
  featureSupportsBattery: 'Whether battery metadata is reported or required.',
  featureSupportsAlerts: 'Whether threshold or health alerting is supported.',
  featureSupportsHistory: 'Whether time-series history is expected.',
  featureSupportsLocation: 'Whether location metadata is meaningful for this type.',
  featureSupportsCalibration: 'Whether calibration settings are supported.',
  featureSupportsFwota: 'Whether firmware over-the-air updates are supported.',
  powerSource: 'How the device is powered: battery, mains, solar, or hybrid.',
  batteryVariableKey: 'Canonical telemetry key that represents battery state (e.g. bat).',
  batteryUnit: 'Reporting unit such as % or V.',
  batteryReference: 'Battery part number for maintenance/replacement.',
  batteryFullValue: 'Value representing a full charge (mainly for voltage-based batteries).',
  batteryLowThreshold: 'Low battery warning threshold.',
  batteryCriticalThreshold: 'Critical battery threshold.',
  batteryLifetime: 'Nominal battery life for ops planning.',
  batteryReplaceable: 'Whether field replacement is supported.',
  externalAllowedTypes: 'External sensor profile names that may attach, comma-separated.',
  externalVariableKeys: 'Canonical keys provided by those sensors.',
  externalMaxCount: 'Maximum external sensors per device.',
  externalRequired: 'Device is incomplete without at least one attached sensor.',
  expectedInterval: 'Normal uplink cadence in seconds.',
  offlineAfter: 'Age of last telemetry after which the device is considered offline.',
  timestampUnit: 'Always epoch milliseconds for this platform.',
  variableKey: 'Canonical API field name for this measurement.',
  variableLabel: 'Human-readable name in UIs.',
  variableUnit: 'Display/storage unit.',
  ingestionKeys: 'Raw payload keys mapped to this canonical key (comma-separated).',
  variableSource:
    'Where the value comes from: raw device, derived, external sensor, or computed.',
  variableDataType: 'number, boolean, string, or enum.',
  variableAvailability: 'always / optional / only when an external sensor is present.',
  variableRequired: 'Whether this variable is expected on a healthy device profile.',
  normalMin: 'Minimum value considered normal for healthy operation.',
  normalMax: 'Maximum value considered normal for healthy operation.',
  warningLow: 'Low warning bound for healthy-usage monitoring.',
  warningHigh: 'High warning bound for healthy-usage monitoring.',
  criticalLow: 'Low critical operating-condition bound.',
  criticalHigh: 'High critical operating-condition bound.',
} as const;

export const manufacturedHelp = {
  mac: 'Wi‑Fi/base MAC as 12 uppercase hex digits (e.g. A1B2C3D4E5F6). Optional : or - are stripped. To register multiple devices, enter MACs separated by commas only (e.g. AABBCCDDEEFF,112233445566). At most 100 MACs per registration.',
  batchRegister: 'OPEN manufacturing batch that owns this MAC. Date and size come from the batch.',
  typesRegister: 'Pick exactly one catalog type from the batch for this MAC.',
  notesItem: 'Manufacturing/QA notes; optional.',
  hardwareStatus:
    'AVAILABLE = stock; DESTROYED/FAULTY = not for customers; DEV_USE = lab; SOLD = sold to a client (not yet bound); BOUND = actively bound to a client.',
  typesCreateBatch:
    'Catalog types included in this production run (drives batch ID brand segment at create).',
  typesEditBatch:
    'Catalog types allowed for MACs in this batch. You can add types anytime; removing a type is blocked if a registered MAC still uses it. Batch ID is unchanged.',
  batchDate: 'Production date (YYYY-MM-DD); used in batch ID.',
  batchSize: 'How many MACs this batch can hold.',
  batchStatus: 'OPEN = can register MACs; CLOSED = no new registrations.',
  notesBatch: 'Optional batch-level notes.',
} as const;

export const clientsHelp = {
  displayName: 'Customer/org name shown in Admin and portals.',
  clientType: 'organization / individual / partner — drives which Core flows apply.',
  address: 'Legal or billing address. Locked after first save (fill-if-empty).',
  nit: 'Tax ID / NIT. Locked after first save.',
  city: 'City. Locked after first save (fill-if-empty).',
  country: 'Country. Locked after first save (fill-if-empty).',
  masterId:
    'Optional Core users.user_id for the org master. Prefer Sync after create when a master exists.',
  status: 'active = normal; suspended = access blocked at client level.',
} as const;

export const staffHelp = {
  email: 'Cognito username; invite email is sent here.',
  displayName: 'Name shown in Staff list and profile.',
  role: 'admin = full access; operator = only granted permissions.',
  status:
    'ACTIVE = can sign in; DISABLED = Cognito disabled (no HQ login). Prefer Suspend icon for quick disable.',
  clientsPerms: 'Clients: view = see list/details; create = add clients; edit = change client fields/status.',
  deviceCatalogPerms:
    'Device catalog: create = add device types; edit = change existing type definitions.',
  manufacturingBatchesPerms:
    'Manufacturing batches: create = open new batches; edit = change date/size/status/notes/types.',
  manufacturedDevicesPerms:
    'Manufactured devices: create = register MACs; edit = change hardware status/notes and sync IoT; telemetry_health = open live telemetry health; reset = Reset client / Reset IoT in provisioning.',
  accountPerms:
    'Account: edit = may change own profile after the first automatic edit; first_edit_available = one free profile edit without account.edit (cleared after use).',
  'clients.view': 'See Portal clients.',
  'clients.create': 'Create Portal clients.',
  'clients.edit': 'Edit Portal clients.',
  'device_catalog.create': 'Add device type definitions.',
  'device_catalog.edit': 'Change device type definitions.',
  'manufacturing_batches.create': 'Create production batches.',
  'manufacturing_batches.edit': 'Edit production batches.',
  'manufactured_devices.create': 'Register manufactured MACs.',
  'manufactured_devices.edit': 'Edit hardware records and sync IoT.',
  'manufactured_devices.telemetry_health': 'View live telemetry health (last seen, connection, data health).',
  'manufactured_devices.reset':
    'Reset client binding or fully reset IoT (Thing/certs) from the provisioning modal.',
  'account.edit': 'May edit own profile after the first automatic edit.',
  'account.first_edit_available':
    'Allows one free profile edit without account.edit (cleared after use).',
} as const;

export const accountHelp = {
  displayName: 'How you appear to other staff.',
  phone: 'Contact number (optional).',
  address: 'Optional mailing/work address.',
  birthDate: 'Optional; format YYYY-MM-DD.',
  photo: 'PNG or JPEG up to 5MB. Leave empty to keep the current photo.',
} as const;
